import { isPlatformBrowser } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    inject,
    OnInit,
    signal,
    input,
    viewChild,
    PLATFORM_ID,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { PdfViewerComponent } from '@warema/shared/components';
import { DeviceInfoService, FileHelperService } from '@warema/shared/helpers';
import { AgreementDocument, AgreementDocumentType } from '@warema/shared/models';
import { DataUtilities } from '@warema/shared/utilities';
import { PopupContent, WaremaButton } from '@warema/warema-ui-angular';
import { MarkdownModule } from 'ngx-markdown';
import { distinctUntilChanged, fromEvent, map } from 'rxjs';

@Component({
    selector: 'warema-agreement-dialog',
    templateUrl: './agreement-dialog.component.html',
    styleUrls: ['./agreement-dialog.component.scss'],
    imports: [PdfViewerComponent, WaremaButton, TranslatePipe, MarkdownModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgreementDialogComponent extends PopupContent<boolean> implements OnInit {
    public readonly agreementDialogDocument = input.required<AgreementDocument>();

    public readonly hideAcceptBtn = input(false);

    public agreementDocumentType = AgreementDocumentType;
    public readonly scrolledToBottom = signal(false);

    private readonly _destroyRef = inject(DestroyRef);
    private readonly _documentReaderRef = viewChild<ElementRef<HTMLElement>>('reader');
    private readonly _fileHelperService: FileHelperService = inject(FileHelperService);
    private readonly _deviceInfoService = inject(DeviceInfoService);
    private readonly _platformId = inject<string>(PLATFORM_ID);

    public ngOnInit(): void {
        if (!this.hideAcceptBtn()) {
            this._initScrollSubscription();
        }
    }

    /**
     * Print function as workaround for a problem on the iPad where the ngx-extended-pdf-viewer does not work correctly
     */
    public async printAgreement(): Promise<void> {
        /**
         * workaround for problems on the iPad where the ngx-extended-pdf-viewer and printJS does not work correctly
         */
        if (this._deviceInfoService.isIos || this._deviceInfoService.isIpadInDesktopMode) {
            window.open(this.agreementDialogDocument().content, '_blank');
        } else if (isPlatformBrowser(this._platformId)) {
            const file = await this._fileHelperService.fetchFile(this.agreementDialogDocument().content);
            const base64Pdf = DataUtilities.arrayBufferToBase64String(await DataUtilities.readBlobAsArrayBuffer(file));

            const printJS = await this._getPrintJs();
            printJS({
                printable: base64Pdf,
                type: 'pdf',
                base64: true,
            });
        } else {
            console.warn('Printing is not supported on the server side.');
        }
    }

    private _initScrollSubscription(): void {
        const documentReader = this._documentReaderRef();

        if (!documentReader?.nativeElement) {
            return;
        }

        this.scrolledToBottom.set(this._isScrolledToBottom());

        fromEvent(documentReader.nativeElement, 'scroll')
            .pipe(
                map(() => this._isScrolledToBottom()),
                distinctUntilChanged(),
                takeUntilDestroyed(this._destroyRef),
            )
            .subscribe(bottomReached => {
                this.scrolledToBottom.set(bottomReached);
            });
    }

    private _isScrolledToBottom(): boolean {
        const documentReader = this._documentReaderRef();
        if (!documentReader?.nativeElement) {
            return false;
        }
        const { scrollHeight, scrollTop, offsetHeight } = documentReader.nativeElement;
        return scrollHeight - Math.ceil(scrollTop) <= offsetHeight;
    }

    private async _getPrintJs(): Promise<(configuration: unknown) => void> {
        const printJsModule = await import('print-js');
        return printJsModule.default as (configuration: unknown) => void;
    }
}
